import { useState, useEffect } from "react";
import axiosInstance from "../../api/axiosInstance";
import { toast } from "react-toastify";

const emptyForm = {
  title: "",
  body: "",
  is_pinned: false,
  publish_date: "",
  expiry_date: "",
};

function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await axiosInstance.get("/api/notifications");
      setNotifications(res.data);
    } catch (err) {
      toast.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingId(null);
    setForm({
      ...emptyForm,
      publish_date: new Date().toISOString().split("T")[0],
    });
    setShowModal(true);
  };

  const openEditModal = (n) => {
    setEditingId(n.id);
    setForm({
      title: n.title,
      body: n.body || "",
      is_pinned: n.is_pinned,
      publish_date: n.publish_date,
      expiry_date: n.expiry_date || "",
    });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const payload = {
      title: form.title,
      body: form.body,
      is_pinned: form.is_pinned,
      publish_date: form.publish_date,
      expiry_date: form.expiry_date || null,
    };

    try {
      if (editingId) {
        await axiosInstance.put(`/api/notifications/${editingId}`, payload);
        toast.success("Notification updated");
      } else {
        await axiosInstance.post("/api/notifications", payload);
        toast.success("Notification created");
      }
      setShowModal(false);
      fetchNotifications();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      await axiosInstance.delete(`/api/notifications/${id}`);
      toast.success("Notification deleted");
      fetchNotifications();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete");
    }
  };

  const statusBadge = (n) => {
    const today = new Date().toISOString().split("T")[0];
    if (n.publish_date > today)
      return <span className="badge bg-secondary">Scheduled</span>;
    if (n.expiry_date && n.expiry_date < today)
      return <span className="badge bg-light text-dark border">Expired</span>;
    return <span className="badge bg-success">Live</span>;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">Notifications</h4>
        <button className="btn btn-primary" onClick={openAddModal}>
          <i className="bi bi-plus-lg me-1"></i> New Notification
        </button>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: "40px" }}></th>
                <th>Title</th>
                <th className="d-none d-md-table-cell">Publish Date</th>
                <th className="d-none d-md-table-cell">Expiry</th>
                <th style={{ width: "90px" }}>Status</th>
                <th style={{ width: "100px" }} className="text-end">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {notifications.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center text-muted py-4">
                    No notifications yet.
                  </td>
                </tr>
              ) : (
                notifications.map((n) => (
                  <tr key={n.id}>
                    <td>
                      {n.is_pinned && (
                        <i className="bi bi-pin-angle-fill text-warning"></i>
                      )}
                    </td>
                    <td className="fw-medium">{n.title}</td>
                    <td className="d-none d-md-table-cell text-muted small">
                      {n.publish_date}
                    </td>
                    <td className="d-none d-md-table-cell text-muted small">
                      {n.expiry_date || "—"}
                    </td>
                    <td>{statusBadge(n)}</td>
                    <td className="text-end">
                      <button
                        className="btn btn-sm btn-outline-secondary me-1"
                        onClick={() => openEditModal(n)}
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(n.id, n.title)}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <>
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <form onSubmit={handleSubmit}>
                  <div className="modal-header">
                    <h5 className="modal-title">
                      {editingId ? "Edit Notification" : "New Notification"}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowModal(false)}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">Title</label>
                      <input
                        type="text"
                        className="form-control"
                        name="title"
                        value={form.title}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Body</label>
                      <textarea
                        className="form-control"
                        name="body"
                        rows="3"
                        value={form.body}
                        onChange={handleChange}
                      ></textarea>
                    </div>
                    <div className="row">
                      <div className="col-6 mb-3">
                        <label className="form-label">Publish Date</label>
                        <input
                          type="date"
                          className="form-control"
                          name="publish_date"
                          value={form.publish_date}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div className="col-6 mb-3">
                        <label className="form-label">Expiry Date</label>
                        <input
                          type="date"
                          className="form-control"
                          name="expiry_date"
                          value={form.expiry_date}
                          onChange={handleChange}
                        />
                        <small className="text-muted">Optional</small>
                      </div>
                    </div>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="is_pinned"
                        name="is_pinned"
                        checked={form.is_pinned}
                        onChange={handleChange}
                      />
                      <label className="form-check-label" htmlFor="is_pinned">
                        Pin to top
                      </label>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={saving}
                    >
                      {saving ? "Saving..." : editingId ? "Update" : "Create"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show"></div>
        </>
      )}
    </div>
  );
}

export default NotificationsPage;
